/**
 * telemetryClient — main-thread wrapper around the telemetry worker.
 *
 * Keeps the fetch+derive burst off the UI thread so the splash animation never
 * hitches. If the worker can't be created (or dies), it transparently falls
 * back to running fetchTelemetry on the main thread so data still loads.
 */

import { fetchTelemetry, type FetchResult } from './sheetsClient';
import type { SheetSource } from '@/config/sheets';
import type {
  TelemetryWorkerRequest,
  TelemetryWorkerResponse,
} from './telemetryWorker';

const WORKER_BROKEN = Symbol('telemetry-worker-broken');

interface Pending {
  resolve: (r: FetchResult) => void;
  reject: (e: unknown) => void;
}

let worker: Worker | null = null;
let workerUnavailable = false;
let seq = 0;
const pending = new Map<number, Pending>();

function failAllPending() {
  for (const [, p] of pending) p.reject(WORKER_BROKEN);
  pending.clear();
}

function getWorker(): Worker | null {
  if (workerUnavailable) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./telemetryWorker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<TelemetryWorkerResponse>) => {
      const msg = e.data;
      const p = pending.get(msg.id);
      if (!p) return;
      pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.result);
      else p.reject(new Error(msg.error));
    };
    worker.onerror = () => {
      // Infrastructure failure — give up on the worker for the whole session
      // and let every caller fall back to the main thread.
      workerUnavailable = true;
      failAllPending();
      worker?.terminate();
      worker = null;
    };
    return worker;
  } catch {
    workerUnavailable = true;
    return null;
  }
}

/**
 * Fetch telemetry off the main thread. On any worker-infra failure this falls
 * back to the main-thread implementation; genuine fetch/parse errors reject.
 */
export function fetchTelemetryAsync(source: SheetSource): Promise<FetchResult> {
  const w = getWorker();
  if (!w) return fetchTelemetry(source);

  return new Promise<FetchResult>((resolve, reject) => {
    const id = ++seq;
    pending.set(id, {
      resolve,
      reject: (e) => {
        if (e === WORKER_BROKEN) {
          // Worker died — retry this request on the main thread.
          fetchTelemetry(source).then(resolve, reject);
        } else {
          reject(e);
        }
      },
    });
    w.postMessage({ id, source } satisfies TelemetryWorkerRequest);
  });
}
